/**
 * ActionFlow Tool Generator — scans block flows, skips init/timer triggers,
 * extracts #{event.xxx} parameter references, and generates execute_* tools.
 */

import type { CogoFlow, CogoFlowStep, CogoBlockDef, CogoBasicStep, CogoExpressionStep } from "@cogo/types"
import type { WebMCPTool, JsonSchema } from "../core/tool-registry"
import type { GeneratorRuntime } from "./form-tools"

// ── Helpers ────────────────────────────────────────────────

/** Extract #{event.xxx} references from flow steps recursively */
function extractEventParams(steps: unknown): Set<string> {
  const params = new Set<string>()
  const eventParamRe = /#\{event\.([a-zA-Z_][a-zA-Z0-9_]*)\}/g

  function scanValue(value: unknown): void {
    if (typeof value === "string") {
      let match: RegExpExecArray | null
      eventParamRe.lastIndex = 0
      while ((match = eventParamRe.exec(value)) !== null) {
        params.add(match[1])
      }
    } else if (Array.isArray(value)) {
      for (const item of value) scanValue(item)
    } else if (value && typeof value === "object") {
      for (const v of Object.values(value)) scanValue(v)
    }
  }

  function scanSteps(steps: unknown): void {
    if (!Array.isArray(steps)) return
    for (const rawStep of steps) {
      if (!rawStep || typeof rawStep !== "object") continue
      const step = rawStep as CogoFlowStep
      switch (step.type) {
        case "basic":
        case "expression": {
          const s = step as CogoBasicStep | CogoExpressionStep
          scanValue(s.params)
          break
        }
        case "conditional":
          scanValue(step.condition)
          scanSteps(step.then)
          if (step.else) scanSteps(step.else)
          break
        case "loop":
          scanValue(step.collection)
          scanSteps(step.steps)
          break
        case "parallel":
          scanSteps(step.steps)
          break
        case "tryCatch":
          scanSteps(step.try)
          scanSteps(step.catch)
          scanSteps(step.finally)
          break
        case "switch":
          scanValue(step.value)
          for (const caseSteps of Object.values(step.cases)) {
            scanSteps(caseSteps)
          }
          if (step.default) scanSteps(step.default)
          break
        case "stage":
          scanSteps(step.onEnter)
          scanSteps(step.steps)
          scanSteps(step.onSuccess)
          scanSteps(step.onError)
          scanSteps(step.onFinally)
          scanSteps(step.compensate)
          break
        case "saga":
          scanSteps(step.stages)
          break
      }
    }
  }

  scanSteps(steps)
  return params
}

/** Detect if flow has side effects (restApi calls, navigate, etc.) */
function hasWriteActions(steps: unknown): boolean {
  const writeActions = new Set(["restApi", "navigate", "showToast", "showDialog", "callFlow"])

  function check(steps: unknown): boolean {
    if (!Array.isArray(steps)) return false
    for (const rawStep of steps) {
      if (!rawStep || typeof rawStep !== "object") continue
      const step = rawStep as CogoFlowStep
      switch (step.type) {
        case "basic":
        case "expression": {
          const s = step as CogoBasicStep | CogoExpressionStep
          if (s.action !== "setState" && s.action !== "compute") {
            if (writeActions.has(s.action)) return true
          }
          break
        }
        case "conditional":
          if (check(step.then)) return true
          if (step.else && check(step.else)) return true
          break
        case "loop":
          if (check(step.steps)) return true
          break
        case "parallel":
          if (check(step.steps)) return true
          break
        case "tryCatch":
          if (check(step.try)) return true
          if (check(step.catch)) return true
          if (check(step.finally)) return true
          break
        case "switch":
          for (const caseSteps of Object.values(step.cases)) {
            if (check(caseSteps)) return true
          }
          if (step.default && check(step.default)) return true
          break
        case "stage":
          if (
            check(step.onEnter) ||
            check(step.steps) ||
            check(step.onSuccess) ||
            check(step.onError) ||
            check(step.onFinally) ||
            check(step.compensate)
          ) return true
          break
        case "saga":
          if (check(step.stages)) return true
          break
      }
    }
    return false
  }

  return check(steps)
}

function sanitizeToolName(raw: string): string {
  return raw
    .replace(/[^a-zA-Z0-9_-]/g, "_")
    .replace(/_+/g, "_")
    .replace(/^_|_$/g, "")
    .toLowerCase()
}

function eventParamsToSchema(paramNames: Set<string>): JsonSchema {
  const properties: Record<string, JsonSchema> = {}
  for (const name of paramNames) {
    properties[name] = {
      type: "string",
      description: `Event parameter: ${name}`,
    }
  }
  return {
    type: "object",
    properties,
    ...(paramNames.size > 0 ? { required: Array.from(paramNames) } : {}),
  }
}

// ── Generator ──────────────────────────────────────────────

export function generateActionFlowTools(
  block: CogoBlockDef,
  flows: Record<string, CogoFlow>,
): WebMCPTool[] {
  const tools: WebMCPTool[] = []

  for (const [flowName, flow] of Object.entries(flows)) {
    // Skip auto-run init flows — only expose interactively-invocable flows as tools.
    if (flow.trigger === "init") continue

    const eventParams = extractEventParams(flow.steps)
    const toolName = `execute_${sanitizeToolName(flowName)}`
    const inputSchema = eventParamsToSchema(eventParams)
    const isReadOnly = !hasWriteActions(flow.steps)

    tools.push({
      name: toolName,
      description: flow.narrative?.effect ?? `Execute flow "${flowName}" (${flow.trigger} trigger)${eventParams.size > 0 ? ` with params: ${Array.from(eventParams).join(", ")}` : ""}`,
      inputSchema,
      handler: createFlowHandler(flowName),
      metadata: {
        blockName: block.blockType,
        readOnly: isReadOnly,
        tags: ["action-flow"],
      },
    })
  }

  return tools
}

function createFlowHandler(
  flowName: string,
): (params: Record<string, unknown>) => (runtime: GeneratorRuntime) => Promise<unknown> {
  return (params: Record<string, unknown>) => {
    return async (runtime: GeneratorRuntime) => {
      return runtime.actionEngine.executeFlow(flowName, params)
    }
  }
}
